<template>
  <div class="bg-white border border-gray-200 rounded-lg p-4 h-full flex flex-col">
    <!-- Переход по уведомлению: реестр показывает одну адресную строку -->
    <div
      v-if="focusCaseId"
      class="flex-shrink-0 mb-3 flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900"
    >
      <span>Показан один случай несоответствия — переход из уведомления.</span>
      <button
        type="button"
        @click="showWholeRegistry"
        class="ml-auto text-blue-700 hover:underline font-medium"
      >
        Показать весь реестр
      </button>
    </div>

    <!-- Фильтры и поиск -->
    <div class="flex-shrink-0 flex flex-wrap gap-3 items-end mb-4">
      <div>
        <label class="block text-xs text-gray-600 mb-1">Источник</label>
        <div class="v-select-filter" style="width: 180px; flex: none;">
          <v-select
            v-model="filters.source"
            :options="sourceOptions"
            :reduce="o => o.value"
            label="label"
            placeholder="Все"
          />
        </div>
      </div>
      <div>
        <label class="block text-xs text-gray-600 mb-1">Состояние</label>
        <div class="v-select-filter" style="width: 230px; flex: none;">
          <v-select
            v-model="filters.state"
            :options="stateOptions"
            :reduce="o => o.value"
            label="label"
            placeholder="Все"
          />
        </div>
      </div>
      <div>
        <label class="block text-xs text-gray-600 mb-1">Номенклатура</label>
        <div class="v-select-filter" style="width: 260px; flex: none;">
          <v-select
            v-model="filters.product_id"
            :options="productOptions"
            :reduce="o => o.value"
            label="label"
            placeholder="Все"
          />
        </div>
      </div>
      <div>
        <label class="block text-xs text-gray-600 mb-1">Обнаружено с</label>
        <input
          type="date"
          v-model="filters.date_from"
          class="px-4 py-2 text-sm border border-gray-300 rounded-lg h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div>
        <label class="block text-xs text-gray-600 mb-1">Обнаружено по</label>
        <input
          type="date"
          v-model="filters.date_to"
          class="px-4 py-2 text-sm border border-gray-300 rounded-lg h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div>
        <label class="block text-xs text-gray-600 mb-1">Поиск</label>
        <input
          type="text"
          v-model="filters.search"
          placeholder="Серийный номер, номер заказа или накладной"
          class="w-64 px-4 py-2 text-sm border border-gray-300 rounded-lg h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <button
        type="button"
        @click="resetFilters"
        class="self-end h-[39px] inline-flex items-center px-4 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium"
      >
        Сбросить
      </button>
    </div>

    <!-- Таблица случаев -->
    <div class="flex-1 overflow-auto">
      <div v-if="loading" class="text-sm text-gray-500">Загрузка...</div>
      <div v-else-if="rows.length === 0" class="text-sm text-gray-500">Несоответствий не найдено</div>
      <table v-else class="w-full text-sm">
        <thead class="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th class="px-2 py-2 text-left border-b whitespace-nowrap">Обнаружено</th>
            <th class="px-2 py-2 text-left border-b whitespace-nowrap">Источник</th>
            <th class="px-2 py-2 text-left border-b">Изделие</th>
            <th class="px-2 py-2 text-left border-b">Заказ или накладная</th>
            <th class="px-2 py-2 text-left border-b">Где обнаружено</th>
            <th class="px-2 py-2 text-left border-b">Кто зафиксировал</th>
            <th class="px-2 py-2 text-left border-b">Описание</th>
            <th class="px-2 py-2 text-left border-b">Документы качества</th>
            <th class="px-2 py-2 text-left border-b">Состояние</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.id"
            :id="'nc-case-' + row.id"
            :class="['border-t border-gray-200 align-top', row.is_stale ? 'bg-amber-50' : '']"
          >
            <td class="px-2 py-1.5 whitespace-nowrap">{{ row.detected_at || '—' }}</td>
            <td class="px-2 py-1.5 whitespace-nowrap">{{ row.source_label }}</td>
            <td class="px-2 py-1.5">
              <div class="text-gray-900">{{ row.product_name }}</div>
              <div class="text-gray-500 text-xs">{{ row.serial_number_label }}</div>
              <div v-if="row.quantity_label" class="text-gray-500 text-xs">{{ row.quantity_label }}</div>
            </td>
            <td class="px-2 py-1.5">
              <router-link
                v-if="row.origin_link"
                :to="row.origin_link"
                class="text-blue-600 hover:underline"
              >
                {{ row.origin_label }}
              </router-link>
              <span v-else>{{ row.origin_label }}</span>
            </td>
            <td class="px-2 py-1.5">{{ row.detected_place }}</td>
            <td class="px-2 py-1.5">{{ row.detected_by }}</td>
            <td class="px-2 py-1.5 max-w-[20rem] break-words" :title="row.description">{{ row.description }}</td>
            <td class="px-2 py-1.5">
              <div v-if="(row.documents || []).length === 0" class="text-gray-400 text-xs">Документов нет</div>
              <div v-else class="space-y-1">
                <div v-for="(document, index) in row.documents" :key="index" class="text-xs">
                  <router-link
                    v-if="document.link"
                    :to="document.link"
                    class="text-blue-600 hover:underline"
                  >
                    {{ document.type_label }} {{ document.number }}
                  </router-link>
                  <span v-else>{{ document.type_label }} {{ document.number }}</span>
                  <span class="text-gray-500"> · {{ document.status_label }}</span>
                  <span v-if="document.date" class="text-gray-400"> · {{ document.date }}</span>
                </div>
              </div>
            </td>
            <td class="px-2 py-1.5">
              <span :class="stateClass(row)" class="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                {{ row.state_label }}
              </span>
              <div v-if="row.stale_label" class="text-xs text-amber-700 mt-1 whitespace-nowrap">
                {{ row.stale_label }}
              </div>
              <div v-if="!row.is_open && row.closed_at" class="text-xs text-gray-500 mt-1 whitespace-nowrap">
                Закрыт {{ row.closed_at }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="flex-shrink-0 mt-2 text-xs text-gray-500">
      Жёлтым выделены случаи без движения дольше недели.
    </p>

    <Pagination
      class="flex-shrink-0 mt-1"
      :current-page="pagination.currentPage"
      :last-page="pagination.lastPage"
      :total="pagination.total"
      :per-page="pagination.perPage"
      @change-page="changePage"
    />
  </div>
</template>

<script>
import axios from 'axios';
import Pagination from '@/components/Pagination.vue';
import { createUrlStateManager } from '@/utils/urlStateManager';

export default {
  components: { Pagination },
  props: {
    moduleId: { type: [String, Number], required: true },
  },
  data() {
    return {
      rows: [],
      loading: false,
      filters: { source: '', state: '', product_id: '', date_from: '', date_to: '', search: '' },
      sourceOptions: [],
      stateOptions: [],
      productOptions: [],
      pagination: { currentPage: 1, perPage: 20, lastPage: 1, total: 0 },
      // Адресный показ одной строки при переходе по уведомлению (?caseId=)
      focusCaseId: null,
    };
  },
  created() {
    // Синхронизация фильтров и страницы с URL — состояние переживает уход в документ и возврат.
    // tab=nonconformity держит активную вкладку рабочего стола СМК.
    this.urlManager = createUrlStateManager(this, {
      'filters.source': { default: '', urlKey: 'source' },
      'filters.state': { default: '', urlKey: 'state' },
      'filters.product_id': { default: '', urlKey: 'product_id' },
      'filters.date_from': { default: '', urlKey: 'date_from' },
      'filters.date_to': { default: '', urlKey: 'date_to' },
      'filters.search': { default: '', debounce: 300, urlKey: 'search' },
      'pagination.currentPage': { type: 'number', default: 1, urlKey: 'page' },
    }, {
      additionalQuery: { tab: 'nonconformity' },
      // Любая правка фильтров выводит из адресного показа: он относится к одному случаю.
      // Загрузка отложена на тик: сброс фильтров меняет сразу несколько значений,
      // и без склейки это был бы залп одинаковых запросов
      onUpdate: (key) => {
        if (key !== 'pagination.currentPage') this.focusCaseId = null;
        this.scheduleLoad();
      },
    });

    this.focusCaseId = this.readCaseIdFromRoute();
    this.load();
  },
  unmounted() {
    clearTimeout(this._loadTimer);
  },
  watch: {
    'filters.source'() { this.pagination.currentPage = 1; },
    'filters.state'() { this.pagination.currentPage = 1; },
    'filters.product_id'() { this.pagination.currentPage = 1; },
    'filters.date_from'() { this.pagination.currentPage = 1; },
    'filters.date_to'() { this.pagination.currentPage = 1; },
    // Клик по уведомлению, когда пользователь уже на столе СМК: меняется только query
    '$route.query.caseId'(value) {
      const caseId = Number(value);
      if (!caseId) return;
      this.focusCaseId = caseId;
      this.pagination.currentPage = 1;
      this.load();
    },
  },
  methods: {
    /**
     * Идентификатор случая из адреса перехода по уведомлению
     */
    readCaseIdFromRoute() {
      const caseId = Number(this.$route.query.caseId);
      return caseId > 0 ? caseId : null;
    },
    /**
     * Склеить несколько изменений фильтров в одну загрузку
     */
    scheduleLoad() {
      clearTimeout(this._loadTimer);
      this._loadTimer = setTimeout(() => this.load(), 0);
    },
    async load() {
      this.loading = true;
      try {
        // В адресном показе фильтры не применяются: строка должна найтись при любых
        // значениях, оставшихся в URL с прошлого визита
        const payload = this.focusCaseId
          ? { case_id: this.focusCaseId }
          : { ...this.cleanFilters(), page: this.pagination.currentPage, per_page: this.pagination.perPage };

        const { data } = await axios.post('/api/nonconformity/getCases', payload);
        if (data?.success) {
          const page = data.data || {};
          this.rows = page.rows || [];
          const pagination = page.pagination || {};
          // В адресном показе номер страницы не трогаем: он остаётся от полного реестра,
          // куда пользователь вернётся кнопкой «Показать весь реестр»
          if (!this.focusCaseId) this.pagination.currentPage = pagination.current_page || 1;
          this.pagination.lastPage = pagination.last_page || 1;
          this.pagination.total = pagination.total || 0;
          this.pagination.perPage = pagination.per_page || this.pagination.perPage;

          const references = page.references || {};
          this.sourceOptions = references.sources || [];
          this.stateOptions = references.states || [];
          this.productOptions = references.products || [];

          if (this.focusCaseId) this.scrollToCase(this.focusCaseId);
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка загрузки реестра несоответствий');
      } finally {
        this.loading = false;
      }
    },
    /**
     * Выйти из адресного показа и вернуться к полному реестру
     */
    showWholeRegistry() {
      this.focusCaseId = null;
      this.pagination.currentPage = 1;
      const query = { ...this.$route.query };
      delete query.caseId;
      this.$router.replace({ query }).catch(() => {});
      this.scheduleLoad();
    },
    /**
     * Прокрутить к строке случая и подсветить её
     */
    scrollToCase(caseId) {
      this.$nextTick(() => {
        const element = document.getElementById('nc-case-' + caseId);
        if (!element) return;
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-2', 'ring-blue-400');
        setTimeout(() => element.classList.remove('ring-2', 'ring-blue-400'), 2500);
      });
    },
    cleanFilters() {
      const out = {};
      for (const [key, value] of Object.entries(this.filters)) {
        if (value !== '' && value != null) out[key] = value;
      }
      return out;
    },
    changePage(page) {
      this.pagination.currentPage = page;
    },
    resetFilters() {
      this.filters.source = '';
      this.filters.state = '';
      this.filters.product_id = '';
      this.filters.date_from = '';
      this.filters.date_to = '';
      this.filters.search = '';
      this.pagination.currentPage = 1;
      if (this.focusCaseId) {
        this.showWholeRegistry();
        return;
      }
      this.scheduleLoad();
    },
    stateClass(row) {
      if (row.is_stale) return 'bg-amber-100 text-amber-800';
      if (!row.is_open) return 'bg-gray-100 text-gray-700';
      if (row.is_no_investigation) return 'bg-gray-100 text-gray-700';
      return 'bg-yellow-100 text-yellow-700';
    },
    notifyError(error, fallback) {
      const payload = error?.response?.data;
      // Подробности валидации (422) не проглатываем — показываем пользователю
      const details = payload?.errors && typeof payload.errors === 'object'
        ? Object.values(payload.errors).flat().filter(Boolean)
        : [];
      const message = details.length ? details.join(' ') : (payload?.message || fallback || 'Ошибка');
      if (this.$root?.$toast?.error) {
        this.$root.$toast.error(message);
      } else {
        alert(message);
      }
    },
  },
};
</script>
